import { format } from 'date-fns';

import { AspectRatio } from '../types.ts/video';
import { sizeAwareLog } from './datadog';
import { handleDownloadMedia } from './general';
import { LogManager } from './Logger';
import { getMuxHostedThumbnailUrl, getSelfHostedThumbnailUrl } from './story';

import type {
  AssociatedVideo,
  OtherVideo,
  SharedContent,
  Story,
  StoryVideo,
  VideoClip,
} from '../types.ts/story';
import type React from 'react';

/** A clip's published render for one aspect ratio, ready to download. */
export type PublishedClipVariant = { aspectRatio: string; url: string };

/**
 * `associatedVideos` is typed as required but is only populated on *parent*
 * clips — an `AssociatedVideo` variant (e.g. the 9:16 sibling that Content
 * Studio makes the active variant after an aspect-ratio flip) carries no
 * nested array, so guard rather than trusting the type.
 */
export const getAssociatedVideoWithStoryVideoField = (
  video: OtherVideo | AssociatedVideo,
  field: keyof StoryVideo['video'],
): AssociatedVideo | undefined => {
  const options = (video.associatedVideos || []).filter(
    v => v.videoFilePrimary?.video?.[field],
  );
  if (!options.length) {
    return undefined;
  }
  return options.find(o => o.aspectRatio === AspectRatio.AR_16_9) || options[0];
};

export const getThumbnailTime = (
  clipJson: Record<string, any>,
): number | undefined => {
  if (clipJson.startTime == undefined) return;
  return Math.ceil(Number(clipJson.startTime) + 3);
};

export const getThumbnailUrlAndAspectRatio = (
  video: OtherVideo,
  shareableClip: AssociatedVideo | OtherVideo | undefined,
  currentStory: Story | undefined,
  aspectRatioInitial: AspectRatio = AspectRatio.AR_16_9,
): { thumbnailUrl: string | undefined; aspectRatio: AspectRatio } => {
  let aspectRatio = aspectRatioInitial;

  if (shareableClip) {
    if (video.shareableImageId) {
      return {
        thumbnailUrl: video.thumbnail?.url,
        aspectRatio,
      };
    }
    return {
      thumbnailUrl: shareableClip.thumbnail?.url,
      aspectRatio,
    };
  }

  let result =
    video.thumbnail?.url || video.videoFilePrimary?.video?.thumbnailUrl;

  if (!result) {
    const associatedVideo = getAssociatedVideoWithStoryVideoField(
      video,
      'thumbnailUrl',
    );
    if (associatedVideo) {
      result = associatedVideo?.videoFilePrimary?.video?.thumbnailUrl;
      aspectRatio = associatedVideo.aspectRatio;
    }
  }

  if (!result && currentStory?.useAws && video.clipJson) {
    result = getSelfHostedThumbnailUrl(
      currentStory?.originalVideo!,
      getThumbnailTime(video.clipJson),
    );
  } else if (!result && video.clipJson) {
    result = getMuxHostedThumbnailUrl(
      video.clipJson?.muxPlaybackId ||
        currentStory?.originalVideo.video.muxPlaybackId!,
      getThumbnailTime(video.clipJson),
    );
  }

  if (!result) {
    result = currentStory?.thumbnail?.url;
  }
  if (!result) {
    result = currentStory?.originalVideo?.video?.thumbnailUrl;
  }

  return {
    thumbnailUrl: result || '',
    aspectRatio,
  };
};

/**
 * Shared contents for a clip, falling back to the story's clip list — only
 * STORY_QUERY fetches `_allReferencingSharedContents`, so clips taken from
 * DASHBOARD_ALBUM_QUERY (Home cards, Story Manager clip open) lack it.
 *
 * The clip's own value wins, so clips that carry it resolve as they always did.
 * `??` not `||`: `[]` is a real answer (a fresh copy has no shares) and must not
 * fall through. Parent clips only — `associatedVideos` never carries the field.
 */
export const resolveClipSharedContents = (
  clip: OtherVideo | AssociatedVideo | VideoClip | undefined,
  storyClips: OtherVideo[] | undefined,
): SharedContent[] | undefined => {
  const clipId = clip?.id;
  return (
    clip?._allReferencingSharedContents ??
    (clipId
      ? storyClips?.find(v => v.id === clipId)?._allReferencingSharedContents
      : undefined)
  );
};

/**
 * Every published render a clip has, one per aspect ratio — the clip's own
 * record plus its `associatedVideos` siblings. `videoFilePrimary` always points
 * at that aspect ratio's *latest* successful publish (the pipeline overwrites it
 * on each canonical render — see
 * `VideoCreatorStore.applyRenderedPublishRecords`), so there is no version to
 * choose between here.
 *
 * The same `[clip, ...associatedVideos]` walk `downloadVideoClip` does, kept
 * beside it, but yielding the URLs so a caller can take the whole set without
 * first knowing which aspect ratios exist (PLAT-11417).
 */
export const getPublishedClipVariants = (
  clip: OtherVideo | VideoClip,
): PublishedClipVariant[] => {
  const seen = new Set<string>();
  return [clip, ...(clip.associatedVideos || [])]
    .flatMap(video => {
      const aspectRatio = video?.aspectRatio;
      const url = video?.videoFilePrimary?.url;
      if (!aspectRatio || !url || seen.has(aspectRatio)) return [];
      seen.add(aspectRatio);
      return [{ aspectRatio, url }];
    })
    .sort((a, b) => a.aspectRatio.localeCompare(b.aspectRatio));
};

/**
 * Generates a filename for a video clip download
 */
export const getClipFilename = (story?: Story, clip?: VideoClip) =>
  `${story?.title} - ${clip?.title} - ${format(
    new Date(),
    'yyyy-MM-dd HH-mm-ss',
  )}`.replaceAll('.', '');

/**
 * Downloads a video clip in the specified dimension/aspect ratio
 */
export async function downloadVideoClip(
  e: React.MouseEvent<HTMLElement>,
  clip: VideoClip,
  story: Story | undefined,
  dimension: string,
  options?: {
    onDownloadStart?: (dimension: string) => void;
    onDownloadEnd?: () => void;
  },
): Promise<void> {
  e.preventDefault();
  e.stopPropagation();

  try {
    if (
      !clip ||
      (!(clip.associatedVideos && clip.associatedVideos.length > 0) &&
        !clip.videoFilePrimary)
    ) {
      return;
    }

    const videos: (VideoClip | AssociatedVideo)[] = [
      clip,
      ...(clip.associatedVideos || []),
    ].filter((video): video is VideoClip | AssociatedVideo => Boolean(video));

    const videoFile = videos.find(
      video => video.aspectRatio && video.aspectRatio === dimension,
    );

    if (!videoFile?.videoFilePrimary?.url) {
      sizeAwareLog(
        'Missing url on videoFilePrimary during download',
        videoFile,
        console.error,
      );
      return;
    }

    options?.onDownloadStart?.(dimension);

    const media = {
      url: videoFile.videoFilePrimary.url,
      fileName: getClipFilename(story, clip),
      id: clip?.id,
    };

    await handleDownloadMedia(e, media);
  } catch (error) {
    LogManager.getLogger('context:downloadVideoClip').error(
      'Download failed:',
      error,
    );
  } finally {
    options?.onDownloadEnd?.();
  }
}
